import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { meKey, useMe } from '../api/auth';
import { setSignedOutHandler } from '../api/client';

/**
 * Pages inside the app. Not signed in: off to the sign-in page, coming back here after.
 * Must change password: only the change-password page until they do.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isPending } = useMe();
  const location = useLocation();
  const qc = useQueryClient();

  // When a sign-in ends mid-session (expired, signed out elsewhere), show the sign-in page.
  useEffect(() => {
    setSignedOutHandler(() => qc.setQueryData(meKey, null));
  }, [qc]);

  if (isPending) return <FullPageSpinner />;
  if (!me) {
    const next = location.pathname + location.search;
    return <Navigate to={next === '/' ? '/sign-in' : `/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  if (me.mustChangePassword && location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

export function FullPageSpinner() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress size={28} thickness={3} color="inherit" aria-label="Loading" />
    </Box>
  );
}

/** Only accepts a path inside this app, so ?next= can't send people to another site. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\') ? next : '/';
}
